import type { Product } from '@/lib/types';

// Normalizes text so search is accent- and case-insensitive.
export function normalizeText(value: string): string {
  return (value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function tokenize(query: string): string[] {
  const normalized = normalizeText(query);
  if (!normalized) return [];
  return normalized.split(' ').filter(Boolean);
}

const WEIGHTS = {
  nameExact: 120,
  nameStarts: 80,
  nameWordStarts: 60,
  nameContains: 40,
  brandStarts: 26,
  brandContains: 18,
  categoryStarts: 22,
  categoryContains: 14,
  codeExact: 34,
  codeContains: 20,
  descriptionContains: 6,
};

function startsWithWord(text: string, token: string): boolean {
  return text.split(' ').some((word) => word.startsWith(token));
}

// Returns a relevance score for one token, or -1 when the token does not match.
function scoreToken(product: Product, token: string): number {
  const name = normalizeText(product.name);
  const brand = normalizeText(product.brand ?? '');
  const category = normalizeText(product.category?.name ?? '');
  const sku = normalizeText(product.sku ?? '');
  const barcode = normalizeText(product.barcode ?? '');
  const description = normalizeText(product.description ?? '');

  let score = 0;
  let matched = false;

  if (name === token) {
    score += WEIGHTS.nameExact;
    matched = true;
  } else if (name.startsWith(token)) {
    score += WEIGHTS.nameStarts;
    matched = true;
  } else if (startsWithWord(name, token)) {
    score += WEIGHTS.nameWordStarts;
    matched = true;
  } else if (name.includes(token)) {
    score += WEIGHTS.nameContains;
    matched = true;
  }

  if (brand && brand.includes(token)) {
    score += brand.startsWith(token) ? WEIGHTS.brandStarts : WEIGHTS.brandContains;
    matched = true;
  }

  if (category && category.includes(token)) {
    score += category.startsWith(token) ? WEIGHTS.categoryStarts : WEIGHTS.categoryContains;
    matched = true;
  }

  if (sku && sku === token) {
    score += WEIGHTS.codeExact;
    matched = true;
  } else if ((sku && sku.includes(token)) || (barcode && barcode.includes(token))) {
    score += WEIGHTS.codeContains;
    matched = true;
  }

  if (description && description.includes(token)) {
    score += WEIGHTS.descriptionContains;
    matched = true;
  }

  return matched ? score : -1;
}

// All tokens must match (AND). Higher score = more relevant.
export function scoreProduct(product: Product, tokens: string[]): number {
  if (tokens.length === 0) return 1;
  let total = 0;
  for (const token of tokens) {
    const tokenScore = scoreToken(product, token);
    if (tokenScore < 0) return -1;
    total += tokenScore;
  }
  return total;
}

export function searchProducts(products: Product[], query: string): Product[] {
  const tokens = tokenize(query);
  if (tokens.length === 0) return products;

  return products
    .map((product) => ({ product, score: scoreProduct(product, tokens) }))
    .filter((entry) => entry.score >= 0)
    .sort((a, b) => b.score - a.score || a.product.name.localeCompare(b.product.name))
    .map((entry) => entry.product);
}